import type { PlanDto, TargetedPlanDto } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Switch,
  Textarea,
  toast,
} from '@planuze/ui';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePlansContent } from '../../content/sections/plans.js';
import { usePlanForm } from '../../hooks/use-plan-form.js';
import { useUpdatePlan } from '../../hooks/use-plans.js';
import { ApiError } from '../../lib/http.js';
import { CapabilitiesEditor } from './capabilities-editor.js';
import { planCatalogErrorMessage } from './plan-error.js';
import { InheritanceFields } from './plan-form-fields/inheritance-fields.js';

interface Props {
  readonly open: boolean;
  readonly plan: PlanDto | TargetedPlanDto;
  readonly onClose: () => void;
}

/* Edição da metadata mutável do catálogo (ADR-0888). Negociados usam o mesmo PATCH, mas preservam
   alvo/preços/intervalo como identidade imutável; nome acompanha o Product quando houver Stripe. */
export function PlanEditModal({ open, plan, onClose }: Props) {
  const copy = usePlansContent();
  const { locale } = useAdminLocale();
  const form = usePlanForm({ plan });
  const targeted = 'orgId' in plan;
  const update = useUpdatePlan();
  const busy = update.isPending;

  const submit = (): void => {
    update.mutate(
      { id: plan.id, body: form.updateInput },
      {
        onSuccess: () => {
          toast.success(copy.editModal.updatedToast);
          onClose();
        },
        onError: (err) => {
          toast.error(planCatalogErrorMessage(err, locale));
          /* O hook já aguardou a reconciliação das listas. Fechar descarta o expectedUpdatedAt
             obsoleto; a próxima abertura nasce da row recém-buscada e não repete o mesmo 409. */
          if (err instanceof ApiError && err.serverCode === 'plan_concurrent_update') onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent locked={busy}>
        <DialogHeader>
          <DialogTitle>
            {targeted ? copy.editModal.targetedTitle : copy.editModal.title}
          </DialogTitle>
          <DialogDescription>
            {targeted ? copy.editModal.targetedDescription : copy.editModal.description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="pe-display-name">{copy.formFields.displayName}</Label>
            <Input
              id="pe-display-name"
              value={form.displayName}
              placeholder={copy.formFields.displayNamePlaceholder}
              onChange={(event) => form.setDisplayName(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pe-tier">{copy.common.tier}</Label>
              <Input
                id="pe-tier"
                value={form.tier}
                placeholder={copy.common.tierPlaceholder}
                onChange={(e) => form.setTier(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pe-rank">{copy.common.rank}</Label>
              <Input
                id="pe-rank"
                type="number"
                value={String(form.rank)}
                onChange={(e) => form.setRank(Number.parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>
          <InlineAlert variant="info">
            {targeted ? copy.editModal.targetedImmutableNotice : copy.editModal.immutableNotice}
          </InlineAlert>
          {targeted && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-border p-3">
              <div className="col-span-2 space-y-1.5">
                <Label>{copy.formFields.target}</Label>
                <code className="block text-sm text-muted-foreground">
                  {'orgId' in plan ? (plan.orgId ?? plan.userId ?? '—') : '—'}
                </code>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pe-valid-from">{copy.formFields.validFrom}</Label>
                <Input
                  id="pe-valid-from"
                  type="date"
                  value={form.validFrom}
                  onChange={(event) => form.setValidFrom(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pe-valid-until">{copy.formFields.validUntil}</Label>
                <Input
                  id="pe-valid-until"
                  type="date"
                  value={form.validUntil}
                  onChange={(event) => form.setValidUntil(event.target.value)}
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="pe-reason">{copy.editModal.targetedReasonLabel}</Label>
                <Textarea
                  id="pe-reason"
                  rows={3}
                  maxLength={500}
                  value={form.reason}
                  onChange={(event) => form.setReason(event.target.value)}
                />
                {form.reasonChanged && !form.reasonValid && (
                  <p className="text-xs text-destructive">{copy.editModal.targetedReasonError}</p>
                )}
              </div>
            </div>
          )}
          {form.isOrgPlan && (
            <div className="space-y-1.5 rounded-md border border-border p-3">
              <Label htmlFor="pe-included-seats">{copy.formFields.seatsIncluded}</Label>
              <Input
                id="pe-included-seats"
                type="number"
                min={0}
                value={String(form.includedSeats)}
                onChange={(event) =>
                  form.setIncludedSeats(Number.parseInt(event.target.value, 10) || 0)
                }
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{copy.common.capabilities}</Label>
            <CapabilitiesEditor
              value={form.capabilities}
              onChange={form.setCapabilities}
              allowSeats={false}
            />
          </div>
          {targeted ? (
            <InheritanceFields form={form} />
          ) : (
            <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="pe-public" className="text-foreground">
                  {copy.common.publicToggleLabel}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {copy.common.publicToggleDescription}
                </p>
              </div>
              <Switch id="pe-public" checked={form.isPublic} onCheckedChange={form.setIsPublic} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {copy.common.cancel}
          </Button>
          <Button loading={busy} disabled={!form.valid} onClick={submit}>
            {copy.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
